import { CALENDAR_INTERVAL, type CalendarInterval, DateFormat, formatDate } from '@brightlocal/shared';

/** Where a period label is shown: the short axis tick, or the fuller tooltip heading. */
type PeriodLabelPlace = 'axis' | 'tooltip';

/** Date parts of a period's first day, in UTC like the API cuts periods. */
interface PeriodParts {
  /** First day of the period, `YYYY-MM-DD`. */
  date: string;
  /** Short English month, e.g. "Sep". */
  month: string;
  /** Quarter of the year, 1 to 4. */
  quarter: number;
  /** Full year, e.g. "2025". */
  year: string;
  /** Two-digit year, e.g. "25". */
  shortYear: string;
}

// en-US gives "Sep"; en-GB would give "Sept"
const MONTH_FORMAT = new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' });

const PERIOD_LABELS: Record<CalendarInterval, Record<PeriodLabelPlace, (parts: PeriodParts) => string>> = {
  [CALENDAR_INTERVAL.WEEKLY]: {
    axis: ({ date }) => formatDate(date, DateFormat.SHORT),
    tooltip: ({ date }) => formatDate(date, DateFormat.WRITTEN)
  },
  // The full year, because "Sep 26" would read like the 26th of September
  [CALENDAR_INTERVAL.MONTHLY]: {
    axis: ({ month, year }) => `${month} ${year}`,
    tooltip: ({ month, year }) => `${month} ${year}`
  },
  [CALENDAR_INTERVAL.QUARTERLY]: {
    axis: ({ quarter, shortYear }) => `Q${quarter} ${shortYear}`,
    tooltip: ({ quarter, year }) => `Q${quarter} ${year}`
  },
  [CALENDAR_INTERVAL.YEARLY]: {
    axis: ({ year }) => year,
    tooltip: ({ year }) => year
  }
};

/** Inputs of {@link formatPeriodLabel}. */
interface FormatPeriodLabelParams {
  /** First day of the period, `YYYY-MM-DD`. */
  date: string;
  /** Interval the period belongs to. */
  interval: CalendarInterval;
  /** Axis tick or tooltip heading. */
  place: PeriodLabelPlace;
}

/**
 * Labels a period by its first day: "Aug 31" / "Sep 2025" / "Q3 25" / "2025" on the axis,
 * "August 31, 2026" / "Aug 2026" / "Q3 2026" / "2026" in the tooltip.
 */
export const formatPeriodLabel = ({ date, interval, place }: FormatPeriodLabelParams) => {
  const firstDay = new Date(`${date}T00:00:00Z`);
  const year = String(firstDay.getUTCFullYear());

  return PERIOD_LABELS[interval][place]({
    date,
    month: MONTH_FORMAT.format(firstDay),
    quarter: Math.floor(firstDay.getUTCMonth() / 3) + 1,
    year,
    shortYear: year.slice(2)
  });
};
